import { createSlice } from "@reduxjs/toolkit"

const initialState: {
  type: string
  currentFolder: "common" | "custom"
  /** Id of the folder currently being viewed ("" for All Photos/Videos). */
  currentFolderId: string
  /** True while the AI Search tab is the active view (not a real folder). */
  aiSearchActive: boolean
  currentFolderData: Array<{
    id: string
    name: string
    url: string
    folderId: string
    compressedUrl?: string
    b2Url?: string
    b2CompressedUrl?: string
  }>
  selectedPhotos: Array<string>
  fullScreen: boolean
  fullScreenId?: string
  fullScreenObject?: {
    id: string
    name: string
    url: string
    folderId: string
    compressedUrl?: string
    b2Url?: string
    b2CompressedUrl?: string
  }
  shareFile?: string
  deleteArray?: Array<string>
  moveFolderArray?: Array<string>
  addToAlbumArray?: Array<string> | null
  activeAlbumId?: string | null
  logout: boolean
  downloadData: {
    progress: number
    currentPart: number
    totalParts: number
    showModal: boolean
    downloadEnabled: boolean
    individualProgress: {
      currentFile: number
      totalFiles: number
      currentFileProgress: number
      currentFileName: string
    }
  }
  aiSearchData: {
    aiSearchEnabled: boolean
    loading: boolean
    results: Array<{
      id: string
      name: string
      url: string
      folderId: string
      compressedUrl?: string
    }>
  }
  navHeight?: string
  selectionMode: boolean
  switchRoleModal: boolean
  showUploadModal: boolean
  showDownloadAllModal: boolean
  downloadAllFileIds: string[] | null
} = {
  type: "gallery",
  currentFolder: "common",
  currentFolderId: "",
  aiSearchActive: false,
  currentFolderData: [],
  selectedPhotos: [],
  fullScreen: false,
  addToAlbumArray: null,
  activeAlbumId: null,
  logout: false,
  downloadData: {
    progress: 0,
    currentPart: 0,
    totalParts: 0,
    showModal: false,
    downloadEnabled: false,
    individualProgress: {
      currentFile: 0,
      totalFiles: 0,
      currentFileProgress: 0,
      currentFileName: '',
    },
  },
  aiSearchData: {
    aiSearchEnabled: false,
    loading: false,
    results: [],
  },
  selectionMode: false,
  switchRoleModal: false,
  showUploadModal: false,
  showDownloadAllModal: false,
  downloadAllFileIds: null,
}

const utilSlice = createSlice({
  name: "Util",
  initialState,
  reducers: {
    // to set current folder
    setCurrentFolder: (state, action) => {
      state.currentFolder = action.payload
    },

    // to track the id of the folder currently being viewed
    setCurrentFolderId: (state, action) => {
      state.currentFolderId = action.payload
    },

    // to track whether the AI Search view is active
    setAiSearchActive: (state, action) => {
      state.aiSearchActive = action.payload
    },

    // to store data of current folder
    setCurrentFolderData: (state, action) => {
      state.currentFolderData = action.payload
    },

    // to toggle adding or removing selectedPhotos
    setSelectedPhotos: (state, action) => {
      const index = state.selectedPhotos.indexOf(action.payload)

      if (index !== -1)
        state.selectedPhotos = state.selectedPhotos.filter(
          (photo) => photo !== action.payload
        )
      else state.selectedPhotos = [...state.selectedPhotos, action.payload]
    },

    // to remove selected phootos
    resetSelectedPhotos: (state) => {
      state.selectedPhotos = []
      state.shareFile = undefined
      state.moveFolderArray = undefined
      state.deleteArray = undefined
    },

    // to select all photos
    selectAllPhotos: (state, action) => {
      state.selectedPhotos = action.payload.map((cfd: { id: string }) => cfd.id)
    },

    // to set folder type
    setFolderType: (state, action) => {
      state.type = action.payload
    },

    // to set full screen
    setFullScreen: (state, action) => {
      state.fullScreen = action.payload
    },

    // to set full screen
    setFullScreenId: (state, action) => {
      state.fullScreenId = action.payload
    },

    // to set full screen object
    setFullScreenObject: (state, action) => {
      state.fullScreenObject = action.payload
    },

    // to set share file
    setShareFile: (state, action) => {
      state.shareFile = action.payload
    },

    // to set delete array
    setDeleteArray: (state, action) => {
      state.deleteArray = action.payload
    },

    // to set move folder array
    setMoveFolderArray: (state, action) => {
      state.moveFolderArray = action.payload
    },

    // to set the files pending "add to physical album" (opens AddToAlbumModal)
    setAddToAlbumArray: (state, action) => {
      state.addToAlbumArray = action.payload
    },

    // to track the album currently open in the detail view
    setActiveAlbumId: (state, action) => {
      state.activeAlbumId = action.payload
    },

    // to set logout
    setLogout: (state, action) => {
      state.logout = action.payload
    },

    // to set download progress
    setProgress: (state, action) => {
      state.downloadData.progress = action.payload
    },

    // to set download current part
    setCurrentPart: (state, action) => {
      state.downloadData.currentPart = action.payload
    },

    // to set download total parts
    setTotalParts: (state, action) => {
      state.downloadData.totalParts = action.payload
    },

    // to set show download modal
    setShowDownloadModal: (state, action) => {
      state.downloadData.showModal = action.payload
    },

    setShowDownloadAllModal: (state, action) => {
      state.showDownloadAllModal = action.payload
    },

    setDownloadAllFileIds: (state, action) => {
      state.downloadAllFileIds = action.payload
    },

    // to set download enabled or not
    setDownloadEnabled: (state, action) => {
      state.downloadData.downloadEnabled = action.payload
    },

    // to set nav height
    setNavHeight: (state, action) => {
      state.navHeight = `${action.payload}px`
    },
    
    // to toggle selection mode
    setSelectionMode: (state, action) => {
      state.selectionMode = action.payload
    },
    
    // to toggle switch role modal
    setSwitchRoleModal: (state, action) => {
      state.switchRoleModal = action.payload
    },

    // to set AI search enabled
    setAISearchEnabled: (state, action) => {
      state.aiSearchData.aiSearchEnabled = action.payload
    },

    // to set AI search loading
    setAISearchLoading: (state, action) => {
      state.aiSearchData.loading = action.payload
    },

    // to set AI search results
    setAISearchResults: (state, action) => {
      state.aiSearchData.results = action.payload
    },

    // to toggle upload modal
    setShowUploadModal: (state, action) => {
      state.showUploadModal = action.payload
    },

    // to set individual download progress
    setIndividualProgress: (state, action) => {
      state.downloadData.individualProgress = {
        ...state.downloadData.individualProgress,
        ...action.payload,
      }
    },
  },
})

export const {
  setCurrentFolder,
  setCurrentFolderId,
  setAiSearchActive,
  setCurrentFolderData,
  setSelectedPhotos,
  resetSelectedPhotos,
  selectAllPhotos,
  setFolderType,
  setFullScreen,
  setFullScreenId,
  setFullScreenObject,
  setShareFile,
  setDeleteArray,
  setMoveFolderArray,
  setAddToAlbumArray,
  setActiveAlbumId,
  setLogout,
  setCurrentPart,
  setProgress,
  setTotalParts,
  setShowDownloadModal,
  setShowDownloadAllModal,
  setDownloadAllFileIds,
  setDownloadEnabled,
  setNavHeight,
  setSelectionMode,
  setSwitchRoleModal,
  setAISearchEnabled,
  setAISearchLoading,
  setAISearchResults,
  setShowUploadModal,
  setIndividualProgress,
} = utilSlice.actions
export default utilSlice.reducer
